import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { dedupeCreativesById, useCreatives } from 'features/creatives';
import type { CreativeEntry } from 'types/shared';
import { Network } from '../../../constants';

const EMPTY_IDS: string[] = [];
const EMPTY_ROWS: CreativeEntry[] = [];

/**
 * Creatives uploaded to X for the form's `app`. Selected rows are kept in the
 * `$creativesRows` form field so they stay visible (and available for the
 * payload) while a name filter or "selected only" hides them from the API list.
 */
const useTwitterCreativesData = () => {
  const { setValue } = useFormContext();
  const app = useWatch({ name: 'app' }) as string | undefined;
  const selectedCreatives = (useWatch({ name: 'selectedCreatives' }) as string[] | undefined) ?? EMPTY_IDS;
  const selectedOnly = (useWatch({ name: 'selectedCreativesOnly' }) as boolean | undefined) ?? false;
  const filterByName = (useWatch({ name: 'filterCreativeByName' }) as string[] | undefined) ?? EMPTY_IDS;
  const selectedRows =
    (useWatch({ name: '$creativesRows' }) as CreativeEntry[] | undefined) ?? EMPTY_ROWS;

  const { data: apiRows, isLoading, isError, error } = useCreatives({
    config: { enabled: !!app },
    params: { app, network: Network.X, query: filterByName },
  });

  const allRows = useMemo(
    // API order is kept; selected rows the API list no longer contains are appended at the end.
    () => dedupeCreativesById([...(apiRows ?? []), ...selectedRows]),
    [selectedRows, apiRows],
  );

  const rows = useMemo(() => {
    if (!selectedOnly) return allRows;
    const selected = new Set(selectedCreatives);
    return allRows.filter((row) => selected.has(row.id));
  }, [allRows, selectedCreatives, selectedOnly]);

  const setSelection = useCallback(
    (ids: string[]) => {
      const selected = new Set(ids);
      const nextRows = allRows.filter((row) => selected.has(row.id));

      setValue('$creativesRows', nextRows);
      setValue('selectedCreatives', ids, { shouldValidate: true, shouldDirty: true });
    },
    [allRows, setValue],
  );

  useEffect(() => {
    if (!selectedCreatives.length && selectedRows.length) {
      setValue('$creativesRows', EMPTY_ROWS);
    }
  }, [selectedCreatives.length, selectedRows.length, setValue]);

  return {
    rows,
    totalCount: allRows.length,
    selectedCreatives,
    setSelection,
    isLoading: isLoading && !allRows.length,
    isError,
    error,
  };
};

export default useTwitterCreativesData;
